import React from 'react';
import { User, Phone, Mail, MapPin, FileText, Clock, Camera, MessageSquare, Navigation } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import UserAvatar from '../../UserAvatar';
import { STATUS_COLORS } from './StatusTracker';

const SectionCard = ({ title, icon: Icon, testId, children, className = '' }) => (
  <Card className={`border-0 shadow-md ${className}`} data-testid={testId}>
    <CardHeader className="py-3 border-b border-zinc-100">
      <CardTitle className="text-sm font-bold flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#F56F05]" />{title}
      </CardTitle>
    </CardHeader>
    <CardContent className="p-4">{children}</CardContent>
  </Card>
);

export const ClientInfoCard = ({ appt, onOpenCustomer }) => (
  <SectionCard title="Client" icon={User} testId="client-info">
    <div className="flex items-start gap-4">
      <UserAvatar
        user={{ full_name: appt.client_name, email: appt.client_email, photo_url: appt.client_photo_url, role: 'client' }}
        size="lg"
        testId="client-info-avatar"
      />
      <div className="flex-1 grid sm:grid-cols-2 gap-4">
        <div>
          <button
            type="button"
            onClick={() => appt.client_user_id && onOpenCustomer && onOpenCustomer(appt.client_user_id)}
            className="font-semibold text-zinc-900 hover:text-[#F56F05] transition-colors text-left"
            disabled={!appt.client_user_id}
            data-testid="client-profile-link"
          >
            {appt.client_name || 'Unknown'}
          </button>
          {appt.client_phone && <p className="text-sm text-zinc-500 flex items-center gap-1 mt-1"><Phone className="h-3 w-3" />{appt.client_phone}</p>}
          {appt.client_email && <p className="text-sm text-zinc-500 flex items-center gap-1"><Mail className="h-3 w-3" />{appt.client_email}</p>}
        </div>
        {appt.address && (
          <div>
            <p className="text-sm font-medium text-zinc-700 flex items-center gap-1"><MapPin className="h-3 w-3 text-[#F56F05]" />Address</p>
            <p className="text-sm text-zinc-600">{appt.address.line1}</p>
            <p className="text-sm text-zinc-500">{appt.address.city}, {appt.address.state} {appt.address.zip}</p>
            {appt.address.access_notes && (
              <p className="text-xs text-orange-600 mt-1 bg-orange-50 px-2 py-1 rounded">Access: {appt.address.access_notes}</p>
            )}
          </div>
        )}
      </div>
    </div>
  </SectionCard>
);

export const ServiceInfoCard = ({ appt, serviceCost }) => (
  <SectionCard title="Service" icon={FileText} testId="service-info">
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <p className="font-semibold text-zinc-900">{appt.service?.name || 'Unknown'}</p>
          <p className="text-sm text-zinc-500">{appt.service?.duration_minutes || 120} min</p>
        </div>
        <p className="font-bold text-zinc-900">${serviceCost.toFixed(2)}</p>
      </div>
      {(appt.add_ons || []).length > 0 && (
        <div className="border-t border-zinc-100 pt-3">
          <p className="text-xs font-bold text-zinc-500 uppercase mb-2">Add-Ons</p>
          {appt.add_ons.map(a => (
            <div key={a.id} className="flex justify-between text-sm py-1">
              <span className="text-zinc-700">{a.name}</span>
              <span className="text-zinc-900 font-medium">${a.price.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
      {appt.notes_client && (
        <div className="bg-zinc-50 rounded-lg p-3 mt-2">
          <p className="text-xs font-bold text-zinc-500 mb-1">Client Notes</p>
          <p className="text-sm text-zinc-700">{appt.notes_client}</p>
        </div>
      )}
      {appt.notes_internal && (
        <div className="bg-orange-50 rounded-lg p-3">
          <p className="text-xs font-bold text-orange-600 mb-1">Internal Notes</p>
          <p className="text-sm text-zinc-700">{appt.notes_internal}</p>
        </div>
      )}
    </div>
  </SectionCard>
);

export const TimeTrackingCard = ({ timeEntries }) => (
  <SectionCard title="Time Tracking" icon={Clock} testId="time-tracking">
    {timeEntries.length === 0 ? (
      <p className="text-sm text-zinc-400 text-center py-4">No time entries yet</p>
    ) : timeEntries.map(te => (
      <div key={te.id} className="space-y-3">
        {te.cleaner_name && <p className="text-xs font-semibold text-zinc-600" data-testid={`time-entry-guru-${te.id}`}>{te.cleaner_name}{te.entry_source === 'office_correction' ? ` · added by office (${te.corrected_by_name})` : te.corrected_at ? ` · corrected by ${te.corrected_by_name}` : ''}{te.correction_reason ? `: ${te.correction_reason}` : ''}</p>}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-xs text-green-600 font-bold">Clock In</p>
            <p className="font-semibold text-zinc-900">
              {te.clock_in_at ? `${new Date(te.clock_in_at).toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit' })} PT` : '—'}
            </p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-xs text-red-600 font-bold">Clock Out</p>
            <p className="font-semibold text-zinc-900">
              {te.clock_out_at ? `${new Date(te.clock_out_at).toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', minute: '2-digit' })} PT` : '—'}
            </p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-600 font-bold">Total</p>
            <p className="font-semibold text-zinc-900">{te.total_minutes != null ? `${Math.floor(te.total_minutes / 60)}h ${te.total_minutes % 60}m` : '—'}</p>
          </div>
        </div>
        {te.break_minutes > 0 && <p className="text-sm text-zinc-500">Break: {te.break_minutes} min</p>}
        <div className="flex items-center gap-3 text-sm pb-4 border-b border-zinc-100">
          {te.entry_source === 'office_correction' ? (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">Office correction · no GPS</div>
          ) : (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${te.location_verified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            <Navigation className="h-3 w-3" />
            {te.location_verified ? 'GPS Verified' : 'GPS Not Verified'}
          </div>
          )}
          {te.gps_accuracy_meters && (
            <span className="text-zinc-500">Accuracy: {Math.round(te.gps_accuracy_meters)}m</span>
          )}
        </div>
      </div>
    ))}
  </SectionCard>
);

export const PhotoGalleryCard = ({ photos }) => (
  <SectionCard title={`Photos (${photos.length})`} icon={Camera} testId="photo-gallery">
    {photos.length === 0 ? (
      <p className="text-sm text-zinc-400 text-center py-4">No photos uploaded</p>
    ) : (
      <div className="space-y-4">
        {['before', 'after', 'issue'].map(type => {
          const typePhotos = photos.filter(p => p.photo_type === type);
          if (typePhotos.length === 0) return null;
          return (
            <div key={type}>
              <p className="text-xs font-bold text-zinc-500 uppercase mb-2">{type} Photos</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {typePhotos.map(p => (
                  <div key={p.id} className="aspect-square rounded-lg bg-zinc-100 overflow-hidden border border-zinc-200">
                    <img src={p.photo_url} alt={type} className="w-full h-full object-cover"
                      onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </SectionCard>
);

export const TimelineCard = ({ timeline }) => (
  <SectionCard title="Timeline" icon={MessageSquare} testId="timeline">
    {timeline.length === 0 ? (
      <p className="text-sm text-zinc-400 text-center py-4">No updates yet</p>
    ) : (
      <div className="relative pl-6 space-y-4">
        <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-zinc-200" />
        {timeline.map((item, i) => (
          <div key={item.id || i} className="relative">
            <div className={`absolute -left-4 w-3 h-3 rounded-full border-2 border-white ${STATUS_COLORS[item.status] || 'bg-zinc-300'}`} />
            <div className="bg-zinc-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <Badge variant="outline" className="text-[10px]">{item.status}</Badge>
                <span className="text-[10px] text-zinc-400">{new Date(item.created_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', dateStyle: 'medium', timeStyle: 'short' })} PT</span>
              </div>
              {item.note && <p className="text-sm text-zinc-600">{item.note}</p>}
              <p className="text-[10px] text-zinc-400 mt-1">by {item.created_by_name || 'System'}</p>
            </div>
          </div>
        ))}
      </div>
    )}
  </SectionCard>
);
